import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {InlineBanner, InlineBannerTypesEnum} from '../../../components/inlineBanner/inlineBanner';
import {SidebarBox, StyledMultipleButtonWrap} from './sidebarBox';

/*
 * Props.
 */

interface TestDataSidebarProps {
  summary?: ReactNode;
  hasDryRunTestData: boolean;
  isReadOnly: boolean;
  errorMessage?: string;
  onEdit: Handler;
  onDryRun: Handler;
}

/*
 * Style.
 */

const StyledTestDataContent = styled.div`
  margin: 14px 0;
  ${ellipsis()};

  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
`;

const StyledEmptyTestData = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  font-weight: ${fontWeights.semibold};
`;

const StyledInlineBanner = styled(InlineBanner)`
  margin-bottom: 14px;
`;

const StyledErrorMessage = styled.span`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
`;

/*
 * Component.
 */

export const TestDataSidebar: FC<TestDataSidebarProps> = ({
  summary,
  hasDryRunTestData,
  isReadOnly,
  errorMessage,
  onEdit,
  onDryRun,
}) => {
  const theme = useUserTheme();

  return (
    <SidebarBox
      icon={<Icon name="eye" color={theme.palette.pink.shade40} />}
      title={<Trans id="editor-sidebar-testdata-title">Test data</Trans>}
    >
      <StyledTestDataContent>
        {summary || (
          <StyledEmptyTestData>
            <Trans id="editor-sidebar-testdata-empty">No data</Trans>
          </StyledEmptyTestData>
        )}
      </StyledTestDataContent>
      {errorMessage && (
        <StyledInlineBanner
          size={VisualSizesEnum.SMALL}
          type={InlineBannerTypesEnum.ERROR}
          title={<StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
          hasColorfulText
          hasSmallBorderRadius
        />
      )}
      <StyledMultipleButtonWrap>
        <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onEdit} isDisabled={isReadOnly}>
          <Trans id="editor-sidebar-testdata-edit">Edit</Trans>
        </Button>
        {hasDryRunTestData && (
          <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onDryRun}>
            <Trans id="editor-sidebar-testdata-dryrun">Dry run</Trans>
          </Button>
        )}
      </StyledMultipleButtonWrap>
    </SidebarBox>
  );
};
